<template>
  <div>
    <app-title-bar>
      <app-material-icon name="mail" />
      <h4 class="text-text-primary">{{ $t('profile.views.EmailAlias.title') }}</h4>
    </app-title-bar>

    <div class="d-flex flex-column row-gap-6 layout-spacing">
    <AppCard class="pa-6 rounded-lg">
      <h3 class="text-text-primary mb-4">
        {{ $t("profile.views.EmailAlias.emailAddress") }}
      </h3>
      <p class="text-text-secondary">
        {{ $t("profile.views.EmailAlias.yourEmailAddress") }}
        <span class="body-md-bold">
          {{ profile.email }}
        </span>
      </p>
    </AppCard>

    <AppCard class="pa-6 rounded-lg">
      <h3 class="text-text-primary mb-4">
        {{ $t("profile.views.EmailAlias.alias") }}
      </h3>
      <p class="mb-6 text-text-secondary">
        {{ $t("profile.views.EmailAlias.aliasDescription") }}
      </p>

      <h4 class="text-text-primary mb-2">
        {{ $t("profile.views.EmailAlias.addAlias") }}
      </h4>

      <div class="d-flex align-center">
        <AppTextField
          v-model.trim="newAlias"
          placeholder="john.doe@acme.com"
          type="email"
          :maxlength="255"
          style="max-width: 320px"
          :error-messages="emailErrors"
          @blur="validateEmail"
          @keypress.enter="addNewAlias"
        />
        <app-button
          class="ml-4 mb-auto"
          type="outlined"
          variant="neutral"
          :loading="addNewAliasPending"
          :disabled="aliasesPending"
          @click="addNewAlias"
        >
          {{ $t("common.add") }}
        </app-button>
      </div>

      <div v-if="validateAliasPending || aliasesPending" class="text-center">
        <AppLoader small></AppLoader>
      </div>

      <AppCard
        v-else-if="aliasesError || aliases.length"
        class="pa-0 mt-4 rounded-lg"
      >
        <div v-if="aliasesError" class="text-error">
          <app-alert-in-page
            variant="error"
            no-icon
            :dismissible="false"
          >
            {{ $t('profile.views.EmailAlias.getAliasesError') }}
          </app-alert-in-page>
        </div>
        <template v-if="aliases.length">
          <div class="d-flex flex-column user-email-alias">
            <div
              v-for="(alias, index) in aliases"
              :key="index"
              class="section-container d-flex justify-space-between px-5 py-3"
              :class="{
                'border-top-radius': index === 0,
                'border-bottom-radius': index === aliases.length - 1,
                'border-bottom-none': index !== aliases.length - 1,
              }"
            >
              <div class="d-flex flex-column">
                <p class="body-md-bold text-text-primary">
                  {{ alias.email }}
                </p>
                <div v-if="alias.verified">
                  <v-chip
                    variant="flat"
                    density="compact"
                    class="verified-chip"
                  >
                    <div class="d-flex align-center ga-1">
                      <app-material-icon
                        name="check_circle"
                        color="icon-success-bold"
                        filled
                      />
                      <span class="detail-md-bold">{{ $t("profile.views.EmailAlias.verified") }}</span>
                    </div>
                  </v-chip>
                </div>
                <div v-else class="d-flex ga-2">
                  <v-chip
                    variant="flat"
                    density="compact"
                    class="warning-chip"
                  >
                    <div class="d-flex align-center ga-1">
                      <app-material-icon
                        name="warning"
                        color="icon-warning-bold"
                        filled
                      />
                      <span class="detail-md-bold">{{ $t("profile.views.EmailAlias.pendingVerification") }}</span>
                    </div>
                  </v-chip>
                  <template v-if="resendAliasCheckLinkPending[alias.id]">
                    <app-material-icon name="progress_activity" size="xs" class="spinning" />
                  </template>
                  <template v-else>
                    <span
                      class="app-link text-text-link"
                      @click="resendAliasCheckLink(alias.id)"
                    >
                      {{ $t('profile.views.EmailAlias.resendLink') }}
                    </span>
                  </template>
                </div>
              </div>
              <div>
                <app-button
                  variant="danger"
                  @click="openDeleteAliasDialog(alias)"
                >
                  {{ $t('common.delete') }}
                </app-button>
              </div>
            </div>
          </div>
        </template>
      </AppCard>
    </AppCard>

    <EmailAliasDeleteDialog
      v-if="deleteAliasDialog.isOpen"
      :alias="deleteAliasDialog.alias"
      @close="closeDeleteAliasDialog"
    />
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapMutations, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppLoader from '@/common/AppLoader.vue'
import AppTextField from '@/common/AppTextField.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import {
  GET_ALIASES,
  ADD_NEW_ALIAS,
  VALIDATE_USER_ALIAS,
  RESEND_ALIAS_CHECK_LINK,
} from '@/store/modules/user/action_types'
import { SET_DELETE_ALIAS_DIALOG } from '@/store/modules/user/mutation_types'
import { postmarkEmail } from '@/validators/postmarkEmail'

import EmailAliasDeleteDialog from './EmailAliasDeleteDialog.vue'

export default {
  name: 'EmailAlias',
  components: { AppCard, AppLoader, AppTextField, EmailAliasDeleteDialog },
  setup: () => ({ v$: useVuelidate() }),
  validations: {
    newAlias: { required, postmarkEmail },
  },
  computed: {
    ...mapState('user', [
      'profile',
      'aliasesPending',
      'aliases',
      'aliasesError',
      'addNewAliasPending',
      'validateAliasPending',
      'resendAliasCheckLinkPending',
      'deleteAliasDialog',
    ]),
    emailErrors() {
      return formFieldErrors(
        this.v$.newAlias,
        [
          ['postmarkEmail', this.$t('common.validations.email')],
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('profile.views.EmailAlias.addAlias') })],
        ],
      )
    },
  },
  data() {
    return {
      newAlias: '',
    }
  },
  created () {
    const token = this.$route.params.token
    if (token) {
      // If the token is present, validate the user email alias
      this.validateUserAlias(token)
    } else {
      this.getAlias()
    }
  },
  watch: {
    newAlias (newval) {
      // Do not display error when the email field is empty
      if (newval === '') {
        this.v$.newAlias.$reset()
      }
    },
  },
  methods: {
    ...mapActions('user', [
      GET_ALIASES,
      ADD_NEW_ALIAS,
      VALIDATE_USER_ALIAS,
      RESEND_ALIAS_CHECK_LINK,
    ]),
    ...mapMutations('user', [SET_DELETE_ALIAS_DIALOG]),
    async getAlias () {
      try {
        await this.GET_ALIASES()
      } catch (e) {
        toast.error(this.$t('profile.views.EmailAlias.getAliasesError'))
      }
    },
    async validateUserAlias (token) {
      try {
        const response = await this.VALIDATE_USER_ALIAS({ token })

        toast.success(this.$t('profile.views.EmailAlias.validateAliasSuccess', { email: response?.data?.email }))
      } catch (e) {
        if (e?.response?.data?.errorCode === 'ERR_INVALID_TOKEN') {
          toast.error(this.$t('profile.views.EmailAlias.validateAliasExpired'))
        } else if (e?.response?.data?.errorCode === 'ERR_NOT_FOUND') {
          toast.error(this.$t('profile.views.EmailAlias.validateAliasAfterDeletion'))
        } else {
          toast.error(this.$t('profile.views.EmailAlias.validateAliasError'))
        }
      } finally {
        this.getAlias()
      }
    },
    validateEmail () {
      this.v$.newAlias.$touch()
      // Do not display error when leaving the empty email field
      if (this.newAlias === '') {
        this.v$.newAlias.$reset()
      }
    },
    async addNewAlias () {
      this.v$.newAlias.$touch()
      if (!this.v$.newAlias.$invalid) {
        try {
          await this.ADD_NEW_ALIAS({
            email: this.newAlias,
          })
          toast.success(this.$t('profile.views.EmailAlias.sendEmailSuccess', { email: this.newAlias }))
          this.newAlias = ''
        } catch (e) {
          if (e?.response?.data?.errorCode === 'ERR_EXISTING_EMAIL') {
            toast.error(this.$t('profile.views.EmailAlias.addAliasErrorExistingEmail'))
          } else {
            toast.error(this.$t('profile.views.EmailAlias.addAliasError'))
          }
        }
      }
    },
    async resendAliasCheckLink (aliasId) {
      try {
        await this.RESEND_ALIAS_CHECK_LINK({ aliasId })

        toast.success(this.$t('profile.views.EmailAlias.verificationLinkSentSuccess'))
      } catch (e) {
        console.error(e)
        toast.error(this.$t('profile.views.EmailAlias.verificationLinkSentError'))
      }
    },
    openDeleteAliasDialog (alias) {
      this.SET_DELETE_ALIAS_DIALOG({
        alias,
        isOpen: true,
      })
    },
    closeDeleteAliasDialog () {
      this.SET_DELETE_ALIAS_DIALOG({
        alias: null,
        isOpen: false,
      })
    },
  },
}
</script>

<style lang="scss">
@import "@/scss/border-tokens";

.user-email-alias {
  .section-container:not(:last-child) {
    border-bottom: get-border();
  }
  .border-top-radius {
    border-top-left-radius: get-radius('border-radius-3');
    border-top-right-radius: get-radius('border-radius-3');
  }
  .border-bottom-radius {
    border-bottom-left-radius: get-radius('border-radius-3');
    border-bottom-right-radius: get-radius('border-radius-3');
  }
  .border-bottom-none {
    border-bottom: none;
  }

  .verified-chip {
    background-color: rgb(var(--v-theme-surface-success));
    color: rgb(var(--v-theme-text-success));
  }

  .warning-chip {
    background-color: rgb(var(--v-theme-surface-warning));
    color: rgb(var(--v-theme-text-warning));
  }

  .app-link:hover {
    color: rgb(var(--v-theme-text-link-hover)) !important;
  }
}

</style>
